import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Send, Loader2, Compass, Save } from 'lucide-react';
import { useAuth } from '../../../../contexts/AuthContext';
import { ChatMessage, ChatHistoryMessage, ResponseBlock } from './types';
import { MessageBubble } from './blocks';
import { claudeQueryService, ChatRequestDTO } from '../../../../services/claudeQueryService';

const QUICK_PROMPTS = [
    { label: 'Lagerstatus', prompt: 'Vis totalbeholdning per vare, sortert etter antall' },
    { label: 'Åpne ordrer', prompt: 'Hvor mange åpne salgsordrer har vi?' },
    { label: 'Plukkestatistikk', prompt: 'Vis plukkestatistikk per operatør siste 7 dager' },
    { label: 'Mottak i dag', prompt: 'Vis alle mottak som er gjort i dag' },
];

interface ChatInterfaceProps {
    conversationId?: number | null;
    onConversationSaved?: (id: number) => void;
    onSaveAsReport?: (sql: string, prompt: string) => void;
}

export const ChatInterface: React.FC<ChatInterfaceProps> = ({
    conversationId = null,
    onConversationSaved,
    onSaveAsReport,
}) => {
    const { user } = useAuth();
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [conversationTitle, setConversationTitle] = useState<string | null>(null);
    const [currentConversationId, setCurrentConversationId] = useState<number | null>(conversationId ?? null);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [loadingConversation, setLoadingConversation] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 128)}px`;
        }
    }, [inputValue]);

    // Load conversation from backend when conversationId is provided
    useEffect(() => {
        if (conversationId && conversationId > 0) {
            loadConversation(conversationId);
        }
    }, [conversationId]);

    const loadConversation = async (id: number) => {
        try {
            setLoadingConversation(true);
            const detail = await claudeQueryService.getConversation(id);

            // Parse messages JSON
            if (detail.messages) {
                try {
                    const parsed = JSON.parse(detail.messages) as ChatMessage[];
                    // Restore Date objects
                    const restored = parsed.map(m => ({
                        ...m,
                        timestamp: new Date(m.timestamp),
                    }));
                    setMessages(restored);
                } catch {
                    setMessages([]);
                }
            }

            setConversationTitle(detail.title || null);
            setCurrentConversationId(detail.conversationId);
            setHasUnsavedChanges(false);
        } catch (err) {
            console.error('Failed to load conversation:', err);
        } finally {
            setLoadingConversation(false);
        }
    };

    // Auto-save: debounce after each assistant response
    const scheduleAutoSave = useCallback(() => {
        if (autoSaveTimerRef.current) {
            clearTimeout(autoSaveTimerRef.current);
        }
        autoSaveTimerRef.current = setTimeout(() => {
            saveConversation(true);
        }, 3000); // Auto-save 3 seconds after last message
    }, []);

    // Cleanup auto-save timer on unmount
    useEffect(() => {
        return () => {
            if (autoSaveTimerRef.current) {
                clearTimeout(autoSaveTimerRef.current);
            }
        };
    }, []);

    const saveConversation = async (isAutoSave = false) => {
        // Don't save empty conversations or during loading
        const realMessages = messages.filter(m => !m.isLoading);
        if (realMessages.length === 0) return;

        try {
            if (!isAutoSave) setIsSaving(true);

            // Serialize messages to JSON (strip non-serializable data for storage)
            const serializableMessages = realMessages.map(m => ({
                id: m.id,
                role: m.role,
                content: m.content,
                timestamp: m.timestamp,
                blocks: m.blocks,
                generatedSQL: m.generatedSQL,
                executionTimeMs: m.executionTimeMs,
                tokensUsed: m.tokensUsed,
                cacheHit: m.cacheHit,
                suggestedFollowups: m.suggestedFollowups,
                isError: m.isError,
            }));

            // Determine title: use first user message or conversationTitle from API
            const title = conversationTitle
                || realMessages.find(m => m.role === 'user')?.content.substring(0, 100)
                || 'Ny samtale';

            const result = await claudeQueryService.saveConversation({
                conversationId: currentConversationId && currentConversationId > 0
                    ? currentConversationId
                    : undefined,
                title,
                messages: JSON.stringify(serializableMessages),
                messageCount: realMessages.length,
            });

            if (result.conversationId && result.conversationId !== currentConversationId) {
                setCurrentConversationId(result.conversationId);
                onConversationSaved?.(result.conversationId);
            }

            setConversationTitle(title);
            setHasUnsavedChanges(false);
        } catch (err) {
            console.error('Failed to save conversation:', err);
        } finally {
            if (!isAutoSave) setIsSaving(false);
        }
    };

    const buildConversationHistory = (): ChatHistoryMessage[] => {
        return messages
            .filter(m => !m.isLoading)
            .slice(-10)
            .map(m => ({
                role: m.role as 'user' | 'assistant',
                content: m.content,
                hasData: !!(m.blocks && m.blocks.some(b => b.type === 'table')),
                dataSummary: m.blocks?.find(b => b.type === 'table')?.tableData
                    ? `${m.blocks.find(b => b.type === 'table')!.tableData!.rows.length} rader`
                    : undefined,
                generatedSQL: m.generatedSQL,
            }));
    };

    const handleSubmit = async (messageText?: string) => {
        const text = messageText || inputValue.trim();
        if (!text || isLoading || !user?.companyNo) return;

        const userMessage: ChatMessage = {
            id: Date.now().toString(),
            role: 'user',
            content: text,
            timestamp: new Date(),
        };

        setMessages(prev => [...prev, userMessage]);
        setInputValue('');
        setIsLoading(true);
        setHasUnsavedChanges(true);

        // Auto-derive title from first user message
        if (!conversationTitle && messages.length === 0) {
            setConversationTitle(text.substring(0, 100));
        }

        const loadingId = (Date.now() + 1).toString();
        setMessages(prev => [...prev, {
            id: loadingId,
            role: 'assistant',
            content: '',
            timestamp: new Date(),
            isLoading: true,
        }]);

        try {
            const request: ChatRequestDTO = {
                message: text,
                conversationHistory: buildConversationHistory(),
                maxRows: 100,
            };

            const response = await claudeQueryService.sendMessage(request);

            const assistantMessage: ChatMessage = {
                id: loadingId,
                role: 'assistant',
                content: response.message,
                timestamp: new Date(),
                blocks: response.blocks as ResponseBlock[],
                generatedSQL: response.generatedSQL,
                executionTimeMs: response.executionTimeMs,
                tokensUsed: response.tokensUsed,
                cacheHit: response.cacheHit,
                suggestedFollowups: response.suggestedFollowups,
                isError: !response.success,
            };

            setMessages(prev => prev.map(m => m.id === loadingId ? assistantMessage : m));

            // Update title from API response if provided
            if (response.conversationTitle) {
                setConversationTitle(response.conversationTitle);
            }

            // Schedule auto-save after successful response
            scheduleAutoSave();
        } catch (error) {
            const errorMessage: ChatMessage = {
                id: loadingId,
                role: 'assistant',
                content: error instanceof Error ? error.message : 'Det oppstod en feil ved kommunikasjon med serveren.',
                timestamp: new Date(),
                isError: true,
            };

            setMessages(prev => prev.map(m => m.id === loadingId ? errorMessage : m));
        } finally {
            setIsLoading(false);
        }
    };

    const handleFormSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        handleSubmit();
    };

    const lastAssistantMessage = [...messages].reverse().find(m => m.role === 'assistant' && !m.isLoading);
    const showQuickPrompts = messages.length === 0 && !loadingConversation;
    const suggestedFollowups = lastAssistantMessage?.suggestedFollowups?.filter(Boolean) || [];

    return (
        <div className="flex flex-col h-full bg-white rounded-lg shadow">
            {/* Conversation header (when we have a title or unsaved changes) */}
            {(conversationTitle || hasUnsavedChanges) && messages.length > 0 && (
                <div className="flex items-center justify-between px-4 py-2 border-b bg-gray-50 rounded-t-lg">
                    <span className="text-xs text-gray-500 truncate max-w-[300px]">
                        {conversationTitle || 'Ny samtale'}
                    </span>
                    <button
                        onClick={() => saveConversation(false)}
                        disabled={isSaving || !hasUnsavedChanges}
                        className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors ${
                            hasUnsavedChanges
                                ? 'text-blue-600 hover:bg-blue-50 bg-blue-50/50'
                                : 'text-gray-400'
                        }`}
                        title="Lagre samtale"
                    >
                        {isSaving ? (
                            <Loader2 size={12} className="animate-spin" />
                        ) : (
                            <Save size={12} />
                        )}
                        {hasUnsavedChanges ? 'Lagre' : 'Lagret'}
                    </button>
                </div>
            )}

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {loadingConversation ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400">
                        <Loader2 size={24} className="animate-spin text-blue-500 mb-3" />
                        <p className="text-sm">Laster samtale...</p>
                    </div>
                ) : messages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400">
                        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center mb-4">
                            <Compass size={32} className="text-white" />
                        </div>
                        <p className="text-lg font-medium mb-1 text-gray-600">Hei! Jeg er Compass</p>
                        <p className="text-sm text-gray-400 mb-6">Still et spørsmål om lagerdataene dine</p>
                    </div>
                ) : null}

                {messages.map((message, idx) => {
                    if (message.isLoading) {
                        return (
                            <div key={message.id} className="flex justify-start">
                                <div className="mr-2 mt-1 flex-shrink-0">
                                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                                        <Compass size={16} className="text-white" />
                                    </div>
                                </div>
                                <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm">
                                    <div className="flex items-center gap-2">
                                        <Loader2 size={16} className="animate-spin text-blue-600" />
                                        <span className="text-sm text-gray-500">Tenker...</span>
                                    </div>
                                </div>
                            </div>
                        );
                    }

                    // Find the preceding user message for assistant messages (used for "Save as report")
                    let userPrompt = '';
                    if (message.role === 'assistant') {
                        for (let i = idx - 1; i >= 0; i--) {
                            if (messages[i].role === 'user') {
                                userPrompt = messages[i].content;
                                break;
                            }
                        }
                    }

                    return (
                        <MessageBubble
                            key={message.id}
                            message={message}
                            onSaveAsReport={onSaveAsReport}
                            userPrompt={userPrompt}
                            onDrillDown={(query) => handleSubmit(query)}
                        />
                    );
                })}

                <div ref={messagesEndRef} />
            </div>

            {/* Suggested followups */}
            {suggestedFollowups.length > 0 && !isLoading && (
                <div className="px-4 pb-2 flex gap-2 flex-wrap">
                    {suggestedFollowups.map((followup, idx) => (
                        <button
                            key={idx}
                            onClick={() => handleSubmit(followup)}
                            className="px-3 py-1.5 text-xs bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors border border-gray-200"
                        >
                            {followup}
                        </button>
                    ))}
                </div>
            )}

            {/* Quick prompts (only shown when empty) */}
            {showQuickPrompts && (
                <div className="px-4 pb-2 flex gap-2 flex-wrap justify-center">
                    {QUICK_PROMPTS.map((qp, idx) => (
                        <button
                            key={idx}
                            onClick={() => handleSubmit(qp.prompt)}
                            className="px-3 py-1.5 text-xs bg-blue-50 text-blue-700 rounded-full hover:bg-blue-100 transition-colors border border-blue-200"
                        >
                            {qp.label}
                        </button>
                    ))}
                </div>
            )}

            {/* Input Area */}
            <div className="border-t p-4 bg-gray-50 rounded-b-lg">
                <form onSubmit={handleFormSubmit} className="flex gap-2">
                    <textarea
                        ref={textareaRef}
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleSubmit();
                            }
                        }}
                        placeholder="Still et spørsmål om lagerdataene..."
                        className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none min-h-[48px] max-h-32"
                        rows={1}
                        disabled={isLoading || loadingConversation}
                    />
                    <button
                        type="submit"
                        disabled={!inputValue.trim() || isLoading || loadingConversation}
                        className="px-5 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                    >
                        {isLoading ? (
                            <Loader2 size={20} className="animate-spin" />
                        ) : (
                            <Send size={20} />
                        )}
                    </button>
                </form>
                <p className="text-xs text-gray-400 mt-1.5 text-center">
                    Enter for å sende, Shift+Enter for ny linje
                </p>
            </div>
        </div>
    );
};
